import type React from "react"
import { useEffect, useRef, useState } from "react"

type BlurTarget =
    | React.RefObject<HTMLElement>
    | React.RefObject<HTMLElement>[]
    | null

interface Options {
    threshold?: number // in pixels
}

/**
 * Blurs textarea(s) on visualViewport scroll when touching.
 * Idea taken from typescripts/../mobileDmWindowManager.tsx.
 * Some browsers show weird behavior (like scrolling underneath the viewport) when scrolling around the page while the keyboard is open. Blur the input when the viewport scrolls from user interaction.
 *
 * @param targets - A ref or array of refs to blur. If null, all textareas will be blurred.
 * @param options - Options including optional scroll threshold.
 */
export function useBlurOnViewportScroll(
    targets: BlurTarget,
    options: Options = {},
): void {
    const { threshold = 0 } = options
    const [isTouching, setIsTouching] = useState(false)
    const lastScrollYRef = useRef(window.visualViewport?.offsetTop ?? 0)

    useEffect(() => {
        const handleTouchStart = () => setIsTouching(true)
        const handleTouchEnd = () => setIsTouching(false)

        window.addEventListener("touchstart", handleTouchStart)
        window.addEventListener("touchend", handleTouchEnd)

        return () => {
            window.removeEventListener("touchstart", handleTouchStart)
            window.removeEventListener("touchend", handleTouchEnd)
        }
    }, [])

    useEffect(() => {
        if (typeof window.visualViewport === "undefined") return

        const handleScroll = () => {
            if (!isTouching) return

            const currentY = window.visualViewport?.offsetTop ?? 0
            const deltaY = Math.abs(currentY - lastScrollYRef.current)

            if (deltaY >= threshold) {
                if (Array.isArray(targets)) {
                    targets.forEach((ref) => ref.current?.blur())
                } else if (targets?.current) {
                    targets.current.blur()
                } else {
                    document
                        .querySelectorAll(
                            "input, textarea, select, [contenteditable='true']",
                        )
                        .forEach((el) => (el as HTMLElement).blur())
                }
            }

            lastScrollYRef.current = currentY
        }

        window.visualViewport?.addEventListener("scroll", handleScroll)
        return () => {
            window.visualViewport?.removeEventListener("scroll", handleScroll)
        }
    }, [isTouching, targets, threshold])
}
